import Stack from '@mui/material/Stack';
import Box from '@mui/system/Box';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GradientTypography } from 'src/components/gradients/GradientTypography';
import { TwoColumnLayout } from 'src/components/TwoColumnLayout';
import { useMobile } from 'src/hooks/useMobile';
import { useWalletAuth } from 'src/hooks/useWalletAuth';
import {
  AuthenticationAgreements,
  AuthenticationAgreementsFormValues,
} from '../AuthenticationAgreements';
import { AuthenticationCloseButton } from '../AuthenticationCloseButton';
import {
  AuthenticationFlowPageId,
  AuthenticationFlowPageProps,
} from '../AuthenticationFlow';
import type { LoginMethodType } from '../login-stack/LoginMethods';
import { LoginStack } from '../login-stack/LoginStack';
import type { RootState } from '../store';
import { setLoginMethod, setRegistrationStartData } from '../store/authSlice';

type AuthenticationStartingPageProps = AuthenticationFlowPageProps & {
  closeModal: () => void;
};

export type AuthenticationStartingPageUserData =
  AuthenticationAgreementsFormValues;

const loginMethodToNextPageMap: {
  [K in LoginMethodType]?: AuthenticationFlowPageId;
} = {
  phone: 'enter phone',
  email: 'enter email',
  wallet: undefined,
};

export function AuthenticationRegistrationStartPage({
  pageId: pageID,
  onPageComplete,
  closeModal,
}: AuthenticationStartingPageProps) {
  const { signInWallet } = useWalletAuth();
  const isMobile = useMobile();
  const [showAgreementErrorMsg, setShowAgreementErrorMsg] = useState(false);
  const startingFormValues = useSelector((state: RootState) => {
    return state['registration start'];
  });
  const [formValues, setFormValues] =
    useState<AuthenticationStartingPageUserData>(
      startingFormValues || {
        agreedPrivacyPolicy: false,
        agreedTermsOfUse: false,
        agreed13OrOlder: false,
      },
    );

  const dispatch = useDispatch();

  if (!pageID || !onPageComplete || !closeModal) return null;

  const onFormChange = (valueID: string, value: boolean) => {
    if (formValues.hasOwnProperty(valueID))
      setFormValues({ ...formValues, [valueID]: value });
  };

  const signInFn = async (loginMethodType: LoginMethodType) => {
    // Are we ready to move on? Have they accepted all the necessary agreements?
    let hasAcceptedAllAgreements = true;
    Object.keys(formValues).forEach((agreement: string) => {
      if (!formValues[`${agreement}`]) hasAcceptedAllAgreements = false;
    });

    if (!hasAcceptedAllAgreements) {
      setShowAgreementErrorMsg(true);
    } else {
      setShowAgreementErrorMsg(false);
      await onPageDone(loginMethodType);
    }
  };

  const onPageDone = async (loginMethodType: LoginMethodType) => {
    dispatch(setLoginMethod(loginMethodType));
    dispatch(setRegistrationStartData(formValues));

    if (loginMethodType === 'wallet') {
      signInWallet();
    }

    if (
      loginMethodType !== 'phone' &&
      loginMethodType !== 'email' &&
      loginMethodType !== 'wallet'
    ) {
      // TODO: Do any other processing that is required (connect to the actual oauth)
    }

    onPageComplete(pageID, loginMethodToNextPageMap[`${loginMethodType}`]);
  };

  const Left = (
    <Stack spacing={'24px'} height={'100%'} justifyContent={'center'}>
      {/* <Box flexGrow={'1'} position={'relative'}>
    <Image
      src={'/assets/images/blumint_illustration_logo.png'}
      alt={'Sign in'}
      height={400}
      width={316}
      objectFit={'contain'}
    />
  </Box> */}
      <Box>
        <AuthenticationAgreements
          onFormChange={onFormChange}
          currentValues={formValues}
          shouldShowError={showAgreementErrorMsg}
        />
      </Box>
    </Stack>
  );

  const Right = (
    <Stack
      spacing={8}
      // alignContent="center"
      // justifyContent="center"
      sx={{ height: '100%' }}
    >
      <Box
        width={'100%'}
        display={'flex'}
        flexDirection={'row'}
        alignItems="center"
      >
        <Box flexGrow={1}>
          <GradientTypography
            gradientColor="primary.vertical"
            typography={'h4'}
            textAlign={'center'}
            textTransform={'uppercase'}
            whiteSpace="nowrap"
          >
            Sign In / Sign Up
          </GradientTypography>
        </Box>
        <AuthenticationCloseButton closeModal={closeModal} />
      </Box>
      <LoginStack signInFn={signInFn} topOptions={['email']} />
    </Stack>
  );

  return !isMobile ? (
    <TwoColumnLayout
      spaceBetween={9.5}
      wrapperSX={{ minWidth: { xs: '500px', md: '670px' } }}
      left={Left}
      right={Right}
    />
  ) : (
    <Stack spacing="32px">
      {Right}
      {Left}
    </Stack>
  );
}
